import { jsx, jsxs } from "react/jsx-runtime";
import { LoadingMask, Table } from "@zillow/constellation-10";
import { isEmpty } from "lodash";
import PropTypes from "prop-types";
import { useState, useEffect } from "react";
import MortgageRateItem from "./MortgageRateItem.esm.js";
import MortgageRatesTableContainer from "./MortgageRatesTableContainer.esm.js";
import MortgageRatesTableHeader from "./MortgageRatesTableHeader.esm.js";
const ENDPOINT = "https://mortgageapi.zillow.com/getRateTables";
const PROGRAMS = {
  Fixed30Year: "30-Year Fixed Rate",
  Fixed15Year: "15-Year Fixed Rate",
  Fixed20Year: "20-Year Fixed Rate",
  Fixed10Year: "10-Year Fixed Rate",
  ARM7: "7-year ARM",
  ARM5: "5-year ARM",
  ARM3: "3-year ARM"
};
const LOAN_TYPES = {
  Conventional: "Conventional",
  VA: "VA",
  FHA: "FHA",
  USDA: "USDA",
  Other: "Other"
};
const LOAN_SIZES = {
  Micro: "Micro",
  SmallConforming: "Small Conforming",
  Conforming: "Conforming",
  SuperConforming: "Super Conforming",
  Jumbo: "Jumbo"
};
function buildRequestUrl({
  partnerId,
  queries,
  loanToValueBucket,
  creditScoreBucket,
  rateLocation,
  refinance
}) {
  const params = {
    partnerId
  };
  const urlSearchParams = new URLSearchParams(params);
  queries.forEach((query, index) => {
    const key = `q${index}`;
    const queryKey = `queries.${key}`;
    if (query.program) {
      urlSearchParams.append(`${queryKey}.program`, query.program);
    }
    if (query.loanType) {
      urlSearchParams.append(`${queryKey}.loanType`, query.loanType);
    }
    if (query.loanSize) {
      urlSearchParams.append(`${queryKey}.loanAmountBucket`, query.loanSize);
    }
    if (loanToValueBucket) {
      urlSearchParams.append(`${queryKey}.loanToValueBucket`, loanToValueBucket);
    }
    if (creditScoreBucket) {
      urlSearchParams.append(`${queryKey}.creditScoreBucket`, creditScoreBucket);
    }
    if (rateLocation) {
      urlSearchParams.append(`${queryKey}.stateAbbreviation`, rateLocation);
    }
    urlSearchParams.append(`${queryKey}.refinance`, refinance);
  });
  return ENDPOINT + "?" + urlSearchParams.toString();
}
function getMortgageProgramLabel({
  program,
  loanType,
  loanSize
}) {
  let programLabel = PROGRAMS[program];
  if (loanType && loanType !== "Conventional") {
    programLabel += ` ${LOAN_TYPES[loanType]}`;
  }
  if (loanSize && loanSize !== "Conforming") {
    programLabel += ` ${LOAN_SIZES[loanSize]}`;
  }
  return programLabel;
}
function formatRates({
  responseRates,
  queries,
  rateCompare
}) {
  return Object.keys(responseRates).length > 0 ? queries.map(({
    program,
    loanType,
    loanSize,
    link
  }, index) => {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const key = `q${index}`;
    const programLabel = getMortgageProgramLabel({
      program,
      loanType,
      loanSize
    });
    return {
      program: programLabel,
      link: link || null,
      rate: !!responseRates[key].today.rate ? responseRates[key].today.rate.toFixed(2) : responseRates[key].today.rate,
      lastUpdated: (_b = (_a = responseRates[key]) == null ? void 0 : _a.today) == null ? void 0 : _b.time,
      loanAmount: ((_d = (_c = responseRates[key]) == null ? void 0 : _c.todayPayment) == null ? void 0 : _d.loanAmount) || 0,
      downPayment: ((_f = (_e = responseRates[key]) == null ? void 0 : _e.todayPayment) == null ? void 0 : _f.downPayment) || 0,
      monthlyPayment: ((_h = (_g = responseRates[key]) == null ? void 0 : _g.todayPayment) == null ? void 0 : _h.monthlyPayment) || 0,
      isRateChangeUp: responseRates[key].today.rate > responseRates[key][rateCompare].rate,
      rateChange: Math.abs(responseRates[key][rateCompare].rate - responseRates[key].today.rate).toFixed(2),
      apr: responseRates[key].today.apr.toFixed(2),
      isAprChangeUp: responseRates[key].today.apr > responseRates[key][rateCompare].apr,
      aprChange: Math.abs(responseRates[key].today.apr - responseRates[key][rateCompare].apr).toFixed(2)
    };
  }) : [];
}
function useMortgageRatesTable({
  partnerId,
  queries,
  loanToValueBucket,
  creditScoreBucket,
  refinance,
  rateLocation,
  rateCompare,
  nextData
}) {
  const [rates, setRates] = useState((nextData == null ? void 0 : nextData.rates) || []);
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    setIsLoading(true);
    if (!isEmpty(nextData == null ? void 0 : nextData.rates)) {
      setIsLoading(false);
      return;
    }
    const requestUrl = buildRequestUrl({
      partnerId,
      queries,
      loanToValueBucket,
      creditScoreBucket,
      refinance,
      rateLocation
    });
    fetch(requestUrl).then((response) => response.json()).then((data) => {
      const responseRates = data == null ? void 0 : data.rates;
      const formattedRates = formatRates({
        responseRates,
        queries,
        rateCompare
      });
      setRates(formattedRates);
      setIsLoading(false);
    });
  }, [JSON.stringify(queries), partnerId, refinance, loanToValueBucket, creditScoreBucket, rateLocation]);
  return {
    isLoading,
    rates
  };
}
const MortgageRatesTable = ({
  columns,
  refinance,
  partnerId,
  queries,
  loanToValueBucket,
  creditScoreBucket,
  rateLocation,
  rateCompare,
  nextData
}) => {
  var _a, _b;
  const {
    isLoading,
    rates
  } = useMortgageRatesTable({
    refinance,
    partnerId,
    queries,
    loanToValueBucket,
    creditScoreBucket,
    rateLocation,
    rateCompare,
    nextData
  });
  return /* @__PURE__ */ jsx(LoadingMask, { loading: isLoading, css: {
    display: "block"
  }, "aria-busy": isLoading, children: /* @__PURE__ */ jsxs(MortgageRatesTableContainer, { updatedAt: ((_b = new Date((_a = rates[0]) == null ? void 0 : _a.lastUpdated)) == null ? void 0 : _b.toLocaleDateString()) || "N/A", loanToValueBucket, creditScoreBucket, rates, refinance, children: [
    /* @__PURE__ */ jsx(MortgageRatesTableHeader, { rateCompare, columns }),
    /* @__PURE__ */ jsx(Table.Body, { children: isLoading ? /* @__PURE__ */ jsx(Table.Row, { children: /* @__PURE__ */ jsx(Table.Cell, { colSpan: columns.length + 1, children: "Loading..." }) }) : rates.map((formattedRate, index) => {
      return /* @__PURE__ */ jsx(MortgageRateItem, { rate: formattedRate, columns }, index);
    }) })
  ] }) });
};
MortgageRatesTable.Container = MortgageRatesTableContainer;
MortgageRatesTable.Header = MortgageRatesTableHeader;
MortgageRatesTable.Item = MortgageRateItem;
MortgageRatesTable.fetchMortgageRates = async function({
  partnerId,
  queries,
  loanToValueBucket,
  creditScoreBucket,
  refinance,
  rateCompare,
  rateLocation
}) {
  const requestUrl = buildRequestUrl({
    partnerId,
    queries,
    loanToValueBucket,
    creditScoreBucket,
    refinance,
    rateLocation
  });
  const response = await fetch(requestUrl);
  const data = await response.json();
  return formatRates({
    responseRates: data == null ? void 0 : data.rates,
    queries,
    rateCompare
  });
};
MortgageRatesTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.string),
  refinance: PropTypes.bool,
  partnerId: PropTypes.string.isRequired,
  queries: PropTypes.arrayOf(PropTypes.shape({
    program: PropTypes.string,
    loanType: PropTypes.string,
    loanSize: PropTypes.string,
    link: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({
      url: PropTypes.string,
      opensInNewTab: PropTypes.bool
    })])
  })),
  loanToValueBucket: PropTypes.string,
  creditScoreBucket: PropTypes.string,
  rateLocation: PropTypes.string,
  rateCompare: PropTypes.string,
  nextData: PropTypes.shape({
    rates: PropTypes.array
  })
};
MortgageRatesTable.defaultProps = {
  columns: ["rate", "rateChange", "apr", "aprChange"],
  refinance: false,
  queries: [{
    program: "Fixed30Year"
  }],
  rateCompare: "lastWeek",
  loanToValueBucket: "Normal",
  creditScoreBucket: "VeryHigh",
  rateLocation: "US"
};
export {
  LOAN_SIZES,
  LOAN_TYPES,
  PROGRAMS,
  MortgageRatesTable as default,
  formatRates,
  getMortgageProgramLabel,
  useMortgageRatesTable
};
